import React from "react";
import {
  Avatar,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { SouthAfricanRand, toMap } from "../../utils/helpers";
import { CustomerCategory } from "../../utils/constants";
import { COLORS } from "../../constants";
import { head } from "lodash";

const CartSummaryTable = ({
  items,
  pricingData,
  customerCategory,
  handleQuantityChange,
  handleIncrement,
  handleDecrement,
  onRemoveItemClick,
  errorMessages,
}) => {
  const pricingDictionary = toMap(pricingData, (x) => x.sku);
  return (
    <Grid
      container
      alignItems="center"
      spacing={0}
      sx={{
        border: `1px solid ${COLORS.border}`,
        my: 2,
        borderCollapse: "collapse",
      }}
    >
      <Grid
        container
        sx={{
          backgroundColor: COLORS.border,
          color: COLORS.lightText,
          p: 1,
          textAlign: "center",
          borderTop: `1px solid ${COLORS.border}`,
        }}
      >
        <Grid
          item
          xs={5}
          sx={{
            borderRight: `1px solid ${COLORS.border}`,
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2">Product</Typography>
        </Grid>
        <Grid
          item
          xs={2}
          sx={{
            borderRight: `1px solid ${COLORS.border}`,
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2">Price</Typography>
        </Grid>
        <Grid
          item
          xs={2}
          sx={{
            borderRight: `1px solid ${COLORS.border}`,
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2">Quantity</Typography>
        </Grid>
        <Grid
          item
          xs={2}
          sx={{
            borderRight: `1px solid ${COLORS.border}`,
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2">Subtotal</Typography>
        </Grid>
        <Grid
          item
          xs={1}
          sx={{
            borderLeft: "none",
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2">Action</Typography>
        </Grid>
      </Grid>
      {items.map((item) => {
        const productLink = `/product/${encodeURIComponent(item.sku)}`;
        const itemPricingData = head(pricingDictionary?.get(item.sku));

        return (
          <Grid container key={item.sku} sx={{ borderTop: "none" }}>
            <Grid
              item
              xs={2}
              textAlign="center"
              sx={{
                p: 1,
                borderRight: "none",
                borderTop: `1px solid ${COLORS.border}`,
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Link href={productLink}>
                <Avatar
                  src={item.image}
                  alt={item.name}
                  variant="square"
                  sx={{
                    width: { xs: 60, md: 80 },
                    height: { xs: 60, md: 80 },
                  }}
                  imgProps={{
                    style: {
                      objectFit: "contain",
                    },
                  }}
                />
              </Link>
            </Grid>
            <Grid
              item
              xs={3}
              sx={{
                p: 1,
                borderRight: `1px solid ${COLORS.border}`,
                borderTop: `1px solid ${COLORS.border}`,
              }}
            >
              <Link href={productLink} underline="hover">
                <Typography
                  variant="body2"
                  fontWeight="500"
                  color="textPrimary"
                >
                  {item.name}
                </Typography>
              </Link>
              <Typography variant="caption" color="textSecondary">
                SKU: {item.sku}
              </Typography>
            </Grid>
            <Grid
              item
              xs={2}
              textAlign="center"
              sx={{
                p: 1,
                borderRight: `1px solid ${COLORS.border}`,
                borderTop: `1px solid ${COLORS.border}`,
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Typography variant="body2" fontWeight="500">
                {SouthAfricanRand.format(itemPricingData.price)}
              </Typography>
            </Grid>
            <Grid
              item
              xs={2}
              textAlign="center"
              sx={{
                p: 1,
                borderRight: `1px solid ${COLORS.border}`,
                borderTop: `1px solid ${COLORS.border}`,
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <TextField
                variant="outlined"
                type="number"
                value={item.quantity}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none",
                    },
                    "&:hover fieldset": {
                      border: "none",
                    },
                    "&.Mui-focused fieldset": {
                      border: "none",
                    },
                    backgroundColor: COLORS.background,
                    borderRadius: "99px",
                  },
                }}
                onChange={(e) =>
                  handleQuantityChange(item.id, item.sku, e.target.value)
                }
                inputProps={{
                  min: 1,
                  max: 500,
                  step: 1,
                  "aria-label": "quantity",
                  style: {
                    textAlign: "center",
                    fontSize: "0.875rem",
                  },
                }}
                error={!!errorMessages[item.sku]}
                helperText={errorMessages[item.sku]}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton
                        onClick={() =>
                          handleDecrement(item.id, item.sku, item.quantity)
                        }
                        disabled={item.quantity <= 1}
                        sx={{ padding: 0 }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          handleIncrement(item.id, item.sku, item.quantity)
                        }
                        disabled={item.quantity >= 500}
                        sx={{ padding: 0 }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid
              item
              xs={2}
              textAlign="right"
              sx={{
                p: 1,
                borderRight: `1px solid ${COLORS.border}`,
                borderTop: `1px solid ${COLORS.border}`,
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                fontWeight="500"
                sx={{
                  textAlign: "center",
                  color: COLORS.darkText,
                }}
              >
                {SouthAfricanRand.format(itemPricingData.price * item.quantity)}
              </Typography>
              {itemPricingData.invoicePrice > 0 &&
                itemPricingData.invoicePrice !== itemPricingData.price && (
                  <Typography component="p" variant="caption">
                    Invoice:{" "}
                    {SouthAfricanRand.format(
                      itemPricingData.invoicePrice * item.quantity,
                    )}
                  </Typography>
                )}
              {customerCategory !== CustomerCategory.Retail && (
                <Typography component="p" variant="caption">
                  Ex. VAT
                </Typography>
              )}
            </Grid>
            <Grid
              item
              xs={1}
              textAlign="center"
              sx={{
                p: 1,
                borderLeft: "none",
                borderTop: `1px solid ${COLORS.border}`,
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Tooltip title="Remove item" arrow>
                <IconButton
                  color="error"
                  onClick={() => onRemoveItemClick(item.sku)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default CartSummaryTable;
